/**
 * Client-side mirror of functions/src/documents/pdfLayoutRows.js's resolvePdfLayoutRows —
 * a faithful port, not a shared import (functions/ deploys standalone and cannot import
 * web/). Used to build Admin -> PDF Layout's initial editable draft; also carries the
 * pure row-mutation helpers the drag-to-pair/resize/format/hide editor calls, kept here
 * (not inline in the component) so they're unit-testable without React.
 *
 * A row in the DRAFT the editor works with is `{ fields: [{ path, label, type,
 * widthPercent, format }] }` — same shape resolvePdfLayoutRows produces, but every field
 * here also carries its `label`/`type` (needed to render the editor UI, not just the
 * final PDF) rather than being looked up again at render time. Each section also carries
 * `hiddenFields` — fields excluded from the PDF entirely (see hideField/showField below).
 */

import { GRID_COLUMNS, fieldWidth } from './fieldGrid.js';

const DEFAULT_FORMAT = { bold: false, italic: false, underline: false, fontSize: 10 };
export const MIN_FIELD_WIDTH_PERCENT = 10;

/** Column span on the form's six-column grid — fieldGrid.js is the source of truth here;
 * the functions/ resolver replicates this math (keep them in sync). */
const WIDTH_SPANS = { full: 6, half: 3, third: 2 };
const fieldGridSpan = (field) => WIDTH_SPANS[fieldWidth(field)] || WIDTH_SPANS.half;

/**
 * Deterministic simulation of CSS Grid's row-major auto-placement over the intake grid:
 * fields place in order, wrap when a row lacks room for their span, and newRow forces a
 * wrap — mirror of the functions/ copy, see its doc comment for the full contract.
 */
export function formFlowRows(fields) {
  const rows = [];
  let current = [];
  let cursor = 0;
  const flush = () => {
    if (current.length) rows.push(current);
    current = [];
    cursor = 0;
  };
  for (const field of fields) {
    const span = fieldGridSpan(field);
    if (cursor > 0 && (field.newRow === true || cursor + span > GRID_COLUMNS)) flush();
    current.push({ field, widthPercent: Math.round((span / GRID_COLUMNS) * 10000) / 100 });
    cursor += span;
    if (cursor >= GRID_COLUMNS) flush();
  }
  flush();
  return rows;
}

export function resolvePdfLayoutRows(sections, pdfLayout) {
  const layout = pdfLayout && typeof pdfLayout === 'object' && !Array.isArray(pdfLayout) ? pdfLayout : {};
  const sectionOrder =
    layout.sectionOrder && typeof layout.sectionOrder === 'object' && !Array.isArray(layout.sectionOrder)
      ? layout.sectionOrder
      : {};
  const sectionConfigs =
    layout.sections && typeof layout.sections === 'object' && !Array.isArray(layout.sections) ? layout.sections : {};
  const formatByPath =
    layout.format && typeof layout.format === 'object' && !Array.isArray(layout.format) ? layout.format : {};
  const hiddenByPath =
    layout.hidden && typeof layout.hidden === 'object' && !Array.isArray(layout.hidden) ? layout.hidden : {};
  const labelsByPath =
    layout.labels && typeof layout.labels === 'object' && !Array.isArray(layout.labels) ? layout.labels : {};
  const sectionTitles =
    layout.sectionTitles && typeof layout.sectionTitles === 'object' && !Array.isArray(layout.sectionTitles)
      ? layout.sectionTitles
      : {};

  // PDF-only wording override; blank falls back to the form's own label. `baseLabel` is
  // what lets toPdfLayoutDoc persist only genuine diffs — typing the original wording
  // back IS clearing the override.
  const printedLabel = (field) => {
    const raw = labelsByPath[field.path];
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    return trimmed || field.label;
  };

  const withFormat = (field) => {
    const raw = formatByPath[field.path];
    const format =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? {
            bold: raw.bold === true,
            italic: raw.italic === true,
            underline: raw.underline === true,
            fontSize: Number.isFinite(raw.fontSize) && raw.fontSize > 0 ? raw.fontSize : 10,
          }
        : { ...DEFAULT_FORMAT };
    return { ...field, format, baseLabel: field.label, label: printedLabel(field) };
  };

  // Notice banners are instructional copy for the FORM, not an answer — they default to
  // hidden on the PDF even with a completely unset pdfLayout document. `hidden[path] ===
  // false` is the explicit override to print a specific notice anyway.
  const isHidden = (field) => {
    const explicit = hiddenByPath[field.path];
    if (typeof explicit === 'boolean') return explicit;
    return field.type === 'notice';
  };

  const ordered = sections
    .map((section, index) => ({
      section,
      order: Number.isFinite(sectionOrder[section.id]) ? sectionOrder[section.id] : index,
      tie: index,
    }))
    .sort((a, b) => a.order - b.order || a.tie - b.tie)
    .map((entry) => entry.section);

  const followFormRows = layout.followFormRows === true;

  return ordered.map((section) => {
    const visibleFields = section.fields.filter((f) => !isHidden(f));

    if (followFormRows) {
      const rows = formFlowRows(visibleFields).map((rowFields) => ({
        fields: rowFields.map(({ field, widthPercent }) => withFormat({ ...field, widthPercent })),
      }));
      const hiddenFields = section.fields.filter((f) => isHidden(f)).map((f) => withFormat({ ...f, widthPercent: 100 }));
      const rawTitle = sectionTitles[section.id];
      const printedTitle = (typeof rawTitle === 'string' && rawTitle.trim()) || section.title;
      return { id: section.id, title: printedTitle, baseTitle: section.title, rows, hiddenFields };
    }

    const fieldByPath = new Map(visibleFields.map((f) => [f.path, f]));
    const placed = new Set();
    const configuredRows = Array.isArray(sectionConfigs[section.id]?.rows) ? sectionConfigs[section.id].rows : [];

    const rows = [];
    for (const rowConfig of configuredRows) {
      if (!rowConfig || !Array.isArray(rowConfig.fields)) continue;
      const rowFields = [];
      for (const entry of rowConfig.fields) {
        const path = entry && entry.path;
        const field = path ? fieldByPath.get(path) : null;
        if (!field || placed.has(path) || field.type === 'notice' || field.type === 'attachments') continue;
        placed.add(path);
        const widthPercent = Number.isFinite(entry.widthPercent) && entry.widthPercent > 0 ? entry.widthPercent : 100;
        rowFields.push(withFormat({ ...field, widthPercent }));
      }
      if (rowFields.length) rows.push({ fields: rowFields });
    }

    for (const field of visibleFields) {
      if (placed.has(field.path)) continue;
      rows.push({ fields: [withFormat({ ...field, widthPercent: 100 })] });
    }

    const hiddenFields = section.fields.filter((f) => isHidden(f)).map((f) => withFormat({ ...f, widthPercent: 100 }));

    const rawTitle = sectionTitles[section.id];
    const printedTitle = (typeof rawTitle === 'string' && rawTitle.trim()) || section.title;
    return { id: section.id, title: printedTitle, baseTitle: section.title, rows, hiddenFields };
  });
}

/**
 * Converts the editor's in-memory draft back into the settings/pdfLayout v2 document.
 *
 * With `followFormRows` on, the per-section rows config is deliberately NOT persisted:
 * in that mode print rows are recomputed from the form's grid per matter, and freezing
 * the editor's sample arrangement into config would silently defeat the re-flow. Every
 * other kind of edit (order, hiding, format, wording) persists in both modes.
 */
export function toPdfLayoutDoc(sections, { followFormRows = false } = {}) {
  const sectionOrder = {};
  const sectionsConfig = {};
  const format = {};
  const hidden = {};
  const labels = {};
  const sectionTitles = {};
  // A wording override is persisted only when it genuinely differs from the form's own
  // text — so typing the original label/title back clears the override, and a later
  // Field Builder rename flows through to the PDF again instead of being shadowed by a
  // stale copy of the old wording.
  const labelDiff = (f) => {
    const trimmed = (f.label || '').trim();
    if (trimmed && trimmed !== f.baseLabel) labels[f.path] = trimmed;
  };
  sections.forEach((section, index) => {
    sectionOrder[section.id] = index;
    const trimmedTitle = (section.title || '').trim();
    if (trimmedTitle && trimmedTitle !== section.baseTitle) sectionTitles[section.id] = trimmedTitle;
    if (!followFormRows) {
      sectionsConfig[section.id] = {
        rows: section.rows.map((row) => ({
          fields: row.fields.map((f) => ({ path: f.path, widthPercent: f.widthPercent })),
        })),
      };
    }
    section.rows.forEach((row) =>
      row.fields.forEach((f) => {
        const fmt = f.format;
        if (fmt && (fmt.bold || fmt.italic || fmt.underline || fmt.fontSize !== 10)) {
          format[f.path] = fmt;
        }
        // A shown notice needs an explicit `false` — otherwise resolvePdfLayoutRows'
        // "notices default to hidden" rule would hide it again on the next load.
        if (f.type === 'notice') hidden[f.path] = false;
        labelDiff(f);
      })
    );
    section.hiddenFields.forEach((f) => {
      hidden[f.path] = true;
      // A renamed-then-hidden field keeps its wording for when it is shown again.
      labelDiff(f);
    });
  });
  return {
    version: 2,
    ...(followFormRows ? { followFormRows: true } : null),
    sectionOrder,
    sections: sectionsConfig,
    format,
    hidden,
    labels,
    sectionTitles,
  };
}

const clampWidth = (value) => Math.max(MIN_FIELD_WIDTH_PERCENT, Math.min(100, value));

/**
 * Removes one field (by path) from wherever it currently sits (a row — NOT
 * hiddenFields, see hideField/showField for that). A row left empty is dropped
 * entirely; a row left with exactly one field is reset to full width — its old
 * percentage was relative to a row-mate that no longer exists, and a lone field always
 * prints full width regardless of its stored widthPercent (matterRecordPdf.js only
 * consults widthPercent for a row with more than one field), so leaving a stale
 * percentage on it would make the editor's preview visually disagree with the print.
 */
function removeField(sections, sectionId, path) {
  return sections.map((section) => {
    if (section.id !== sectionId) return section;
    const rows = section.rows
      .map((row) => ({ fields: row.fields.filter((f) => f.path !== path) }))
      .filter((row) => row.fields.length > 0)
      .map((row) => (row.fields.length === 1 ? { fields: [{ ...row.fields[0], widthPercent: 100 }] } : row));
    return { ...section, rows };
  });
}

/** The field currently in a ROW at `path` (not a hidden field — those aren't in any row). */
function findField(sections, path) {
  for (const section of sections) {
    for (const row of section.rows) {
      const field = row.fields.find((f) => f.path === path);
      if (field) return field;
    }
  }
  return null;
}

/**
 * Drag-to-pair: moves `path` out of its current spot and merges it into `targetPath`'s
 * row, before or after depending on `side`. Every field in the resulting row is given an
 * equal width share — simplest predictable outcome after a merge; the admin can still
 * drag the dividers afterward to adjust.
 */
export function mergeFieldIntoRow(sections, sectionId, path, targetPath, side) {
  const field = findField(sections, path);
  if (!field || path === targetPath) return sections;
  const withoutField = removeField(sections, sectionId, path);

  return withoutField.map((section) => {
    if (section.id !== sectionId) return section;
    const rows = section.rows.map((row) => {
      if (!row.fields.some((f) => f.path === targetPath)) return row;
      const targetIndex = row.fields.findIndex((f) => f.path === targetPath);
      const insertAt = side === 'left' ? targetIndex : targetIndex + 1;
      const nextFields = [...row.fields];
      nextFields.splice(insertAt, 0, field);
      const evenWidth = 100 / nextFields.length;
      return { fields: nextFields.map((f) => ({ ...f, widthPercent: evenWidth })) };
    });
    return { ...section, rows };
  });
}

/** Drag-to-its-own-line: moves `path` out of its current spot into a new full-width row at `rowIndex`. */
export function moveFieldToOwnRow(sections, sectionId, path, rowIndex) {
  const field = findField(sections, path);
  if (!field) return sections;
  const withoutField = removeField(sections, sectionId, path);

  return withoutField.map((section) => {
    if (section.id !== sectionId) return section;
    const rows = [...section.rows];
    const clampedIndex = Math.max(0, Math.min(rows.length, rowIndex));
    rows.splice(clampedIndex, 0, { fields: [{ ...field, widthPercent: 100 }] });
    return { ...section, rows };
  });
}

/** Drag a divider between two adjacent fields in the same row: opposite-direction resize, clamped. */
export function resizeAdjacentFields(sections, sectionId, rowIndex, leftPath, rightPath, deltaPercent) {
  return sections.map((section) => {
    if (section.id !== sectionId) return section;
    const rows = section.rows.map((row, index) => {
      if (index !== rowIndex) return row;
      const leftIndex = row.fields.findIndex((f) => f.path === leftPath);
      const rightIndex = row.fields.findIndex((f) => f.path === rightPath);
      if (leftIndex === -1 || rightIndex === -1) return row;
      const pool = row.fields[leftIndex].widthPercent + row.fields[rightIndex].widthPercent;
      const nextLeft = clampWidth(row.fields[leftIndex].widthPercent + deltaPercent);
      const boundedLeft = Math.min(nextLeft, pool - MIN_FIELD_WIDTH_PERCENT);
      const nextRight = pool - boundedLeft;
      const fields = row.fields.map((f, i) => {
        if (i === leftIndex) return { ...f, widthPercent: boundedLeft };
        if (i === rightIndex) return { ...f, widthPercent: nextRight };
        return f;
      });
      return { fields };
    });
    return { ...section, rows };
  });
}

/**
 * Trims the editor's title-block draft down to only genuinely-set lines, or `undefined`
 * when every line is blank — an all-default header must not persist an empty object that
 * would read as "configured" forever after. Blank lines fall back to the historical
 * wording at render time (see functions/src/documents/matterRecordPdf.js renderTitleBlock).
 */
export function cleanPdfHeader(header) {
  const raw = header && typeof header === 'object' && !Array.isArray(header) ? header : {};
  const out = {};
  for (const key of ['firmName', 'title', 'tagline', 'numbers']) {
    const trimmed = typeof raw[key] === 'string' ? raw[key].trim() : '';
    if (trimmed) out[key] = trimmed;
  }
  // A boolean is kept either way — `false` ("never print the numbers line") is exactly as
  // deliberate a choice as `true`; only a non-boolean (the editor's "auto") is dropped.
  if (typeof raw.showNumbers === 'boolean') out.showNumbers = raw.showNumbers;
  // showTagline has no auto rule — absent already means shown — so only an explicit
  // false is worth persisting; true would just be configuration noise.
  if (raw.showTagline === false) out.showTagline = false;
  return Object.keys(out).length ? out : undefined;
}

/**
 * PDF-only rename of one field's printed label, wherever it sits (a row or hiddenFields).
 * The draft keeps whatever the admin typed — including blank while mid-edit; blanks fall
 * back to the form label at resolve/persist time rather than printing a nameless field.
 */
export function renameField(sections, path, label) {
  const patch = (f) => (f.path === path ? { ...f, label } : f);
  return sections.map((section) => ({
    ...section,
    rows: section.rows.map((row) => ({ fields: row.fields.map(patch) })),
    hiddenFields: section.hiddenFields.map(patch),
  }));
}

/** PDF-only rename of one section's printed heading. */
export function renameSection(sections, sectionId, title) {
  return sections.map((section) => (section.id === sectionId ? { ...section, title } : section));
}

/** Patches one field's format object, wherever it currently sits (a row or hiddenFields). */
export function patchFieldFormat(sections, path, patch) {
  return sections.map((section) => ({
    ...section,
    rows: section.rows.map((row) => ({
      fields: row.fields.map((f) => (f.path === path ? { ...f, format: { ...f.format, ...patch } } : f)),
    })),
    hiddenFields: section.hiddenFields.map((f) => (f.path === path ? { ...f, format: { ...f.format, ...patch } } : f)),
  }));
}

/** Removes a field from the PDF entirely: pulled out of its row and into that section's hiddenFields list. */
export function hideField(sections, sectionId, path) {
  const field = findField(sections, path);
  if (!field) return sections;
  const withoutField = removeField(sections, sectionId, path);
  return withoutField.map((section) =>
    section.id !== sectionId ? section : { ...section, hiddenFields: [...section.hiddenFields, field] }
  );
}

/** Brings a hidden field back onto the PDF as its own full-width row at the end of its section. */
export function showField(sections, sectionId, path) {
  return sections.map((section) => {
    if (section.id !== sectionId) return section;
    const field = section.hiddenFields.find((f) => f.path === path);
    if (!field) return section;
    return {
      ...section,
      hiddenFields: section.hiddenFields.filter((f) => f.path !== path),
      rows: [...section.rows, { fields: [{ ...field, widthPercent: 100 }] }],
    };
  });
}
